import {createApp} from 'vue/dist/vue.esm-bundler';
import {createPopper} from '@popperjs/core';
import {get} from '../utils/data-manager';

/** Mount the header autocomplete using the same Vue Options API as location search. */
export default class HeaderSearch {
    /**
     * Mount one search form and coordinate it with the header navigation.
     * @param {HTMLElement} $header The containing header.
     * @param {Function} onOpen Close the mobile navigation before opening search.
     */
    constructor($header, onOpen) {
        const $root = $header.querySelector('[data-header-search]');
        if (!$root || $root.headerSearch) {
            return;
        }
        const baseUri = placeholders.router.baseUri;
        const endpoint = new URL(`${baseUri}/ajax/search`, window.location.origin).toString();
        this.$root = $root;
        $root.headerSearch = this;
        this.app = createApp({
            data() {
                return {
                    opened: false,
                    query: '',
                    suggestions: [],
                    loading: false,
                    message: '',
                    activeIndex: -1
                };
            },
            computed: {
                /** Whether a suggestions or feedback panel is needed. */
                visible() {
                    return this.opened && (this.query.trim().length >= 2 || Boolean(this.message));
                },
                /** The selected article, if the visitor used the arrow keys. */
                activeSuggestion() {
                    return this.visible ? this.suggestions[this.activeIndex] : null;
                },
                /** Translated loading, empty and error feedback. */
                status() {
                    if (this.loading) {
                        return $root.dataset.loadingMessage;
                    }
                    return this.message || (this.suggestions.length ? '' : $root.dataset.emptyMessage);
                },
            },
            watch: {
                /** Mount or release Popper when Vue adds or removes the menu. */
                visible() {
                    this.$nextTick(this.positionMenu);
                },
            },
            methods: {
                /** Expand the field, keeping the same search icon visible throughout. */
                async open() {
                    if (this.opened) {
                        return;
                    }
                    onOpen();
                    this.opened = true;
                    $header.classList.add('header--search-open');
                    document.addEventListener('click', this.handleOutsideClick);
                    document.addEventListener('keydown', this.handleEscape);
                    await this.$nextTick();
                    if (this.opened) {
                        this.$refs.input.focus({preventScroll: true});
                        this.suggest();
                    }
                },
                /**
                 * Collapse search and optionally restore focus to its icon.
                 * @param {boolean} restoreFocus Whether dismissal came from Escape.
                 */
                close(restoreFocus = false) {
                    this.opened = false;
                    this.cancel();
                    this.suggestions = [];
                    this.activeIndex = -1;
                    this.message = '';
                    this.loading = false;
                    this.popper?.destroy();
                    this.popper = null;
                    $header.classList.remove('header--search-open');
                    document.removeEventListener('click', this.handleOutsideClick);
                    document.removeEventListener('keydown', this.handleEscape);
                    if (restoreFocus) {
                        this.$nextTick(() => this.$refs.trigger.focus({preventScroll: true}));
                    }
                },
                /** Cancel immediately so stale responses cannot replace the current query. */
                cancel() {
                    this.version++;
                    window.clearTimeout(this.timer);
                    this.request?.abort();
                    this.request = null;
                },
                /**
                 * Update the query and request debounced suggestions.
                 * @param {InputEvent} event The input event.
                 */
                handleInput(event) {
                    this.query = event.target.value;
                    this.suggest();
                },
                /** Start suggestions after two characters and a 300 ms debounce. */
                suggest() {
                    this.cancel();
                    this.activeIndex = -1;
                    this.suggestions = [];
                    this.message = '';
                    const query = this.query.trim();
                    this.loading = this.opened && query.length >= 2;
                    if (this.loading) {
                        const version = this.version;
                        this.timer = window.setTimeout(() => this.loadSuggestions(query, version), 300);
                    }
                },
                /**
                 * Read the existing SearchController response without changing its contract.
                 * @param {string} query The committed search text.
                 * @param {number} version The version owning this request.
                 */
                async loadSuggestions(query, version) {
                    const controller = new AbortController();
                    this.request = controller;
                    try {
                        const response = await get(endpoint, {q: query}, controller.signal, undefined, {rejectErrors: true});
                        if (!Array.isArray(response?.results)) {
                            throw new Error('Invalid search response.');
                        }
                        if (version === this.version && this.opened) {
                            this.suggestions = response.results.slice(0, 5);
                        }
                    } catch (error) {
                        if (error.name !== 'AbortError' && version === this.version && this.opened) {
                            this.message = $root.dataset.errorMessage;
                        }
                    } finally {
                        if (version === this.version) {
                            this.loading = false;
                            this.request = null;
                            this.$nextTick(() => this.popper?.update());
                        }
                    }
                },
                /** Let Popper position the menu using shared dropdown spacing tokens. */
                positionMenu() {
                    this.popper?.destroy();
                    this.popper = null;
                    if (!this.visible || !this.$refs.menu) {
                        return;
                    }
                    const styles = getComputedStyle(this.$refs.menu);
                    const padding = parseFloat(styles.getPropertyValue('--dropdown-viewport-padding')) || 0;
                    this.popper = createPopper(this.$refs.input, this.$refs.menu, {
                        placement: 'bottom-end',
                        modifiers: [
                            {name: 'offset', options: {offset: [0, parseFloat(styles.getPropertyValue('--dropdown-offset')) || 0]}},
                            {name: 'flip', options: {padding}},
                            {name: 'preventOverflow', options: {padding}},
                        ],
                    });
                },
                /** Update dropdown positioning after the field finishes extending. */
                handleTransitionEnd(event) {
                    if (event.target === this.$refs.form && event.propertyName === 'width') {
                        this.popper?.update();
                    }
                },
                /** Close when a pointer action lands outside the search component. */
                handleOutsideClick(event) {
                    if (!$root.contains(event.target)) {
                        this.close();
                    }
                },
                /** Dismiss with Escape and return focus to the search icon. */
                handleEscape(event) {
                    if (event.key === 'Escape') {
                        event.preventDefault();
                        this.close(true);
                    }
                },
                /** Allow focus to transfer from the trigger to the field. */
                handleFocusout() {
                    this.$nextTick(() => {
                        if (this.opened && !$root.contains(document.activeElement)) {
                            this.close();
                        }
                    });
                },
                /** Select article suggestions using the arrow keys. */
                handleKeydown(event) {
                    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) {
                        return;
                    }
                    event.preventDefault();
                    if (!this.suggestions.length) {
                        return;
                    }
                    const step = event.key === 'ArrowDown' ? 1 : -1;
                    this.activeIndex = this.activeIndex < 0
                        ? (step === 1 ? 0 : this.suggestions.length - 1)
                        : (this.activeIndex + step + this.suggestions.length) % this.suggestions.length;
                    this.$nextTick(() => {
                        this.$refs.menu?.querySelector('[aria-selected="true"]')?.scrollIntoView({block: 'nearest'});
                    });
                },
                /** Open the selected article or submit q to the existing CMS search alias. */
                submit(event) {
                    if (this.activeSuggestion) {
                        event.preventDefault();
                        window.location.assign(this.activeSuggestion.url);
                        return;
                    }
                    const query = this.query.trim();
                    if (!query) {
                        event.preventDefault();
                    } else if (!this.$refs.form.getAttribute('action')) {
                        event.preventDefault();
                        this.cancel();
                        this.suggestions = [];
                        this.loading = false;
                    } else {
                        // Native form submission happens before Vue flushes a changed model value.
                        this.$refs.input.value = query;
                    }
                },
            },
            /** Keep external resources outside Vue's deeply reactive state. */
            created() {
                this.version = 0;
                this.request = null;
                this.popper = null;
            },
            /** Enable the icon once the Vue handlers are ready. */
            mounted() {
                this.$refs.trigger.disabled = false;
            },
            /** Release requests, timers, positioning and document listeners. */
            beforeUnmount() {
                this.close();
            },
        });
        this.app.config.compilerOptions.delimiters = ['v{', '}'];
        this.vm = this.app.mount($root);
    }

    /** Close search when the containing header opens its mobile navigation. */
    close() {
        this.vm?.close();
    }

    /** Unmount the autocomplete when removing this header. */
    destroy() {
        this.app?.unmount();
        if (this.$root) {
            delete this.$root.headerSearch;
        }
    }
}
